// Topup checkout/return helpers. Kept separate from utils/checkout.js so the
// shared subscription redirect helper is not altered.

// Query params the backend appends to the topup return URL.
export const TOPUP_RETURN_PARAMS = ["purchaseId", "reference", "status"];

// Survives the redirect so reconciliation can still run if the return URL
// arrives without params. Never treated as authoritative payment state — it is
// only a pointer to the purchase the backend must be asked about.
export const TOPUP_PENDING_PURCHASE_KEY = "topupPendingPurchaseId";

// Classifies a failed POST /topup/checkout into an actionable outcome.
// Prefers a structured code where the backend supplies one and only falls back
// to the documented message text, which the guide specifies verbatim.
export const classifyCheckoutError = (result, httpStatusHint) => {
  const message = String(result?.message || "");
  const lower = message.toLowerCase();
  const code = result?.code;
  const status = httpStatusHint || result?.status || result?.statusCode;

  if (code === "FORBIDDEN" || status === 403) {
    return { kind: "forbidden", message: "Ask your account Admin to buy topups." };
  }
  if (lower.includes("active subscription not found")) {
    return { kind: "no_subscription", message };
  }
  if (lower.includes("billing information")) {
    return { kind: "billing_incomplete", message };
  }
  if (lower.includes("outstanding balance")) {
    return { kind: "outstanding_balance", message };
  }
  if (lower.includes("no longer available") || status === 404) {
    return { kind: "pack_gone", message };
  }
  if (lower.includes("not available on your current plan")) {
    return { kind: "not_on_plan", message };
  }
  if (lower.includes("no pricing configured")) {
    return { kind: "no_country_pricing", message };
  }
  if (lower.includes("quantity must be between")) {
    return { kind: "quantity_out_of_range", message };
  }
  // expiresAt is derived from the subscription's next billing date; without it
  // the backend cannot open a checkout session.
  if (lower.includes("billing date") || lower.includes("nextbillingdate")) {
    return { kind: "no_billing_date", message };
  }
  if (status >= 500) {
    // A purchase row may already exist and have been marked failed before the
    // error surfaced, so the caller must reconcile rather than say "nothing
    // happened".
    return { kind: "server_error", message };
  }
  return { kind: "unknown", message };
};

// Reads the topup return parameters from a query string.
export const readTopupReturn = (search) => {
  const params = new URLSearchParams(search || "");
  return {
    purchaseId: params.get("purchaseId") || "",
    reference: params.get("reference") || "",
    // Advisory only. Never rendered as the outcome and never used to decide
    // success — the backend purchase record is the only truth.
    advisoryStatus: params.get("status") || "",
  };
};

// Strips the topup return params, leaving any unrelated params intact.
export const stripTopupReturnParams = (search) => {
  const params = new URLSearchParams(search || "");
  TOPUP_RETURN_PARAMS.forEach((key) => params.delete(key));
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};
